"use client";

// Inline OS directory icons (folder · file), recreated from the Figma assets
// (public/brand/os/*.svg) as React SVG so they paint INSTANTLY with the page
// (no <img> fetch lag) and tint live to the organism palette — the colored
// folder face and the document's warm wash track --organism-color/core, while
// the paper bodies stay near the egg-white ground. Gradient ids are per-instance
// (useId) so many icons on a page never collide.
//
// Colors are set via `style` (not SVG presentation attributes) because var() /
// color-mix() only resolve in CSS properties, not in fill="" / stop-color="".

import { useId } from "react";

const cream = "color-mix(in oklab, var(--bg) 84%, white)";

export function FolderGlyph({ style }: { style?: React.CSSProperties }) {
  const g = useId();
  return (
    <svg
      viewBox="0 0 81.6531 54.3"
      width="100%"
      height="100%"
      fill="none"
      preserveAspectRatio="none"
      aria-hidden
      style={{ display: "block", filter: "drop-shadow(0 1px 4.45px rgba(0,0,0,0.09))", overflow: "visible", ...style }}
    >
      {/* cream folder back */}
      <path
        d="M11.739 8.81809L8.90343 43.3181C8.85554 43.9007 9.31548 44.4 9.90007 44.4H69.8724C70.4016 44.4 70.8391 43.9878 70.8706 43.4596L72.7512 11.9596C72.7856 11.3849 72.3287 10.9 71.753 10.9H35.6173C35.4199 10.9 35.2268 10.8416 35.0626 10.732L31.0664 8.06795C30.9022 7.95844 30.7092 7.9 30.5117 7.9H12.7357C12.2152 7.9 11.7817 8.29931 11.739 8.81809Z"
        style={{ fill: cream }}
      />
      {/* organism-tinted front flap */}
      <path
        d="M16.6188 15.1463L9.1461 43.6358C8.97899 44.2729 9.46278 44.8948 10.1214 44.8895L70.0275 44.4063C70.4893 44.4026 70.8884 44.0833 70.9933 43.6336L77.5281 15.6272C77.6745 15 77.1984 14.4 76.5543 14.4H17.586C17.1315 14.4 16.7341 14.7066 16.6188 15.1463Z"
        style={{ fill: `url(#${g})` }}
      />
      <defs>
        {/* The front cover WEARS the colour — it is the colour, not a wash of it.
            A folder you tinted purple should read purple across the room; the old
            face was 88% white and every folder looked the same pale cream.
            The only shaping left is a lit falloff (a touch of white at the top-left
            corner, a touch of black at the bottom-right) — the same top-lit
            treatment the colour orb uses, so the cover reads as a solid object
            catching light rather than a flat swatch. */}
        <linearGradient id={g} x1="8.8145" y1="23.2008" x2="76.3145" y2="34.5992" gradientUnits="userSpaceOnUse">
          <stop style={{ stopColor: "color-mix(in oklab, var(--organism-color) 84%, white)" }} />
          <stop offset="1" style={{ stopColor: "color-mix(in oklab, var(--organism-color) 94%, black)" }} />
        </linearGradient>
      </defs>
    </svg>
  );
}

// The page's geometry, shared by FileGlyph and its sibling SkillGlyph so the two
// can never drift apart: the sheet with its top-right corner cut, the fold that
// fills the cut, and the lift that seats the sheet in the folder's footprint.
const SHEET_D =
  "M20.5 10.2C20.5 8.4 21.95 6.95 23.75 6.95H52.4L64.7 19.25V44.1C64.7 45.9 63.25 47.35 61.45 47.35H23.75C21.95 47.35 20.5 45.9 20.5 44.1V10.2Z";
const FOLD_D = "M52.4 6.95L64.7 19.25H55.65C53.85 19.25 52.4 17.8 52.4 16V6.95Z";
// EQUAL-VOLUME LAW: a portrait document only spanned ~54% of the landscape box
// while the folder filled ~84%, so pages read visibly smaller. Scale the whole
// sheet up ~1.18 about its centre so it occupies the same visual volume as the
// folder inside the identical footprint.
const SHEET_LIFT = "translate(42.6 27.15) scale(1.18) translate(-42.6 -27.15)";
// LANDSCAPE document sharing the folder's exact footprint (81.6531 × 54.3), so a
// page and a folder read as the SAME visual size and their labels sit on one
// baseline — no stretch, no distortion (the old portrait sheet was squished into
// the landscape box and threw the icon/label spacing off).
const SHEET_SVG = {
  viewBox: "0 0 81.6531 54.3",
  width: "100%",
  height: "100%",
  fill: "none",
  preserveAspectRatio: "none",
  "aria-hidden": true,
} as const;
const SHEET_SHADOW = "drop-shadow(0 2px 5.5px rgba(0,0,0,0.12))";

/** the sheet's own wash: white-first, a whisper of organism colour deepening to the foot */
function SheetWash({ id }: { id: string }) {
  return (
    <linearGradient id={id} x1="20.5" y1="14" x2="64.7" y2="45" gradientUnits="userSpaceOnUse">
      <stop style={{ stopColor: "color-mix(in oklab, white 95%, var(--organism-color))" }} />
      <stop offset="1" style={{ stopColor: "color-mix(in oklab, white 84%, var(--organism-color))" }} />
    </linearGradient>
  );
}

export function FileGlyph({ style }: { style?: React.CSSProperties }) {
  const g = useId();
  return (
    <svg {...SHEET_SVG} style={{ display: "block", filter: SHEET_SHADOW, overflow: "visible", ...style }}>
      <g transform={SHEET_LIFT}>
        {/* the sheet */}
        <path d={SHEET_D} style={{ fill: `url(#${g})` }} />
        {/* folded top-right corner */}
        <path d={FOLD_D} style={{ fill: "color-mix(in oklab, white 64%, var(--organism-color))" }} />
      </g>
      <defs>
        <SheetWash id={g} />
      </defs>
    </svg>
  );
}

// The spark: a four-point star with soft concave sides, a touch taller than it
// is wide, centred on the cut's diagonal so it straddles the corner (half on the
// sheet, half where the fold used to stand).
const SPARK_D =
  "M58.8 4.9C59.51 10.15 61.25 12.24 65.6 13.1C61.25 13.96 59.51 16.05 58.8 21.3C58.09 16.05 56.35 13.96 52 13.1C56.35 12.24 58.09 10.15 58.8 4.9Z";

/** THE SKILL — FileGlyph's sibling (law_skill_class: a skill is a page wearing a
 *  modifier). The same sheet, the same cut corner, the same wash, shadow and
 *  footprint; only the fold is gone, and in its place stands a spark in the
 *  organism's own colour, lit top-left the way the folder's cover is. At a list
 *  row's size it reads as a point of colour on the page's corner, at a grid's as
 *  the spark. `lit` (hover / drag / select) lets the spark swell a touch. */
export function SkillGlyph({ style, lit = false }: { style?: React.CSSProperties; lit?: boolean }) {
  const g = useId();
  const s = useId();
  return (
    <svg {...SHEET_SVG} style={{ display: "block", filter: SHEET_SHADOW, overflow: "visible", ...style }}>
      <g transform={SHEET_LIFT}>
        <path d={SHEET_D} style={{ fill: `url(#${g})` }} />
        <path
          d={SPARK_D}
          style={{
            fill: `url(#${s})`,
            transformBox: "fill-box",
            transformOrigin: "center",
            transform: lit ? "scale(1.12)" : "none",
            transition: "transform var(--dur) var(--ease-soft)",
          }}
        />
      </g>
      <defs>
        <SheetWash id={g} />
        <linearGradient id={s} x1="52" y1="4.9" x2="65.6" y2="21.3" gradientUnits="userSpaceOnUse">
          <stop style={{ stopColor: "color-mix(in oklab, var(--organism-color) 84%, white)" }} />
          <stop offset="1" style={{ stopColor: "color-mix(in oklab, var(--organism-color) 94%, black)" }} />
        </linearGradient>
      </defs>
    </svg>
  );
}
